/**
 * Timuntu product page: desktop gallery swap + variant label sync
 * + variant-driven gallery (custom.variant_gallery metafield)
 * + in-frame material zoom (3 clicks, no lightbox) + pan when zoomed.
 */
(function () {
  'use strict';

  var ZOOM_SCALES = [1, 2, 3.5];
  var ZOOM_WIDTH = 2500;
  var PAN_THRESHOLD = 8;
  var MOBILE_TAP_THRESHOLD = 10;
  var DESKTOP_MQ = window.matchMedia('(min-width: 900px)');

  function isDesktop() {
    return DESKTOP_MQ.matches;
  }

  function getFrameImage(frame) {
    if (!frame) return null;
    return frame.querySelector('img.timuntu-gallery-image, img.timuntu-thumbnail-image');
  }

  function getMainImage(mainEl) {
    return getFrameImage(mainEl);
  }

  function updateZoomLabel(mainEl, level) {
    if (!mainEl) return;
    var labelIn = mainEl.getAttribute('data-timuntu-zoom-label-in') || '';
    var labelOut = mainEl.getAttribute('data-timuntu-zoom-label-out') || '';
    mainEl.setAttribute('aria-label', level >= 2 ? labelOut : labelIn);
    mainEl.setAttribute('aria-pressed', level > 0 ? 'true' : 'false');
  }

  function getPan(mainEl) {
    return {
      x: Number(mainEl && mainEl._tmPanX) || 0,
      y: Number(mainEl && mainEl._tmPanY) || 0
    };
  }

  function setPan(mainEl, x, y) {
    if (!mainEl) return;
    mainEl._tmPanX = x;
    mainEl._tmPanY = y;
  }

  function readOrigin(img) {
    var ox = 50;
    var oy = 50;
    if (img && img.style.transformOrigin) {
      var parts = img.style.transformOrigin.split(' ');
      var parsedX = parseFloat(parts[0]);
      var parsedY = parseFloat(parts[1]);
      if (!isNaN(parsedX)) ox = parsedX;
      if (!isNaN(parsedY)) oy = parsedY;
    }
    return { x: ox, y: oy };
  }

  function clampPan(mainEl, scale, origin, pan) {
    if (!mainEl || scale <= 1) return { x: 0, y: 0 };
    var rect = mainEl.getBoundingClientRect();
    var w = rect.width || 1;
    var h = rect.height || 1;
    var ox = (origin.x / 100) * w;
    var oy = (origin.y / 100) * h;
    var txMax = ox * (scale - 1);
    var txMin = (w - ox) * (1 - scale);
    var tyMax = oy * (scale - 1);
    var tyMin = (h - oy) * (1 - scale);
    return {
      x: Math.max(txMin, Math.min(txMax, pan.x)),
      y: Math.max(tyMin, Math.min(tyMax, pan.y))
    };
  }

  function compensatePan(mainEl, prevOrigin, nextOrigin, scale, pan) {
    var rect = mainEl.getBoundingClientRect();
    var w = rect.width || 1;
    var h = rect.height || 1;
    var o1x = (prevOrigin.x / 100) * w;
    var o1y = (prevOrigin.y / 100) * h;
    var o2x = (nextOrigin.x / 100) * w;
    var o2y = (nextOrigin.y / 100) * h;
    return {
      x: o1x + (o2x - o1x) * scale + pan.x - o2x,
      y: o1y + (o2y - o1y) * scale + pan.y - o2y
    };
  }

  function paintZoom(mainEl, img, scale, origin, pan) {
    var clamped = clampPan(mainEl, scale, origin, pan || { x: 0, y: 0 });
    setPan(mainEl, clamped.x, clamped.y);
    img.style.transformOrigin = origin.x.toFixed(2) + '% ' + origin.y.toFixed(2) + '%';
    img.style.transform =
      'translate(' + clamped.x.toFixed(2) + 'px, ' + clamped.y.toFixed(2) + 'px) scale(' + scale + ')';
  }

  function syncCarouselLock(frame) {
    if (!frame || !frame.classList.contains('timuntu-thumbnail-item')) return;
    var col = frame.closest('.timuntu-thumbnails-col');
    if (!col) return;
    var zoomed = false;
    var items = col.querySelectorAll('.timuntu-thumbnail-item');
    for (var i = 0; i < items.length; i++) {
      if (parseInt(items[i].getAttribute('data-timuntu-zoom-level') || '0', 10) > 0) {
        zoomed = true;
        break;
      }
    }
    if (zoomed) col.classList.add('is-slide-zoomed');
    else col.classList.remove('is-slide-zoomed');
  }

  function resetAllSlideZooms(root) {
    if (!root) return;
    root.querySelectorAll('.timuntu-thumbnail-item').forEach(function (slide) {
      resetZoom(slide);
    });
  }

  function resetZoom(mainEl) {
    if (!mainEl) return;
    mainEl.setAttribute('data-timuntu-zoom-level', '0');
    mainEl.classList.remove('is-panning');
    setPan(mainEl, 0, 0);
    var img = getMainImage(mainEl);
    if (img) {
      paintZoom(mainEl, img, 1, { x: 50, y: 50 }, { x: 0, y: 0 });
    }
    updateZoomLabel(mainEl, 0);
    syncCarouselLock(mainEl);
  }

  function resolveZoomSrc(img, mainEl) {
    if (img && img.getAttribute('data-timuntu-zoom-src')) {
      return img.getAttribute('data-timuntu-zoom-src');
    }
    if (mainEl && mainEl.getAttribute('data-timuntu-zoom-src')) {
      return mainEl.getAttribute('data-timuntu-zoom-src');
    }
    return '';
  }

  function ensureZoomSrc(img, mainEl) {
    if (!img) return;
    if (img.getAttribute('data-timuntu-zoom-loaded') === '1') return;
    if (img.getAttribute('data-timuntu-zoom-loading') === '1') return;
    var zoomSrc = resolveZoomSrc(img, mainEl);
    if (!zoomSrc) return;
    img.setAttribute('data-timuntu-zoom-loading', '1');
    var probe = new Image();
    probe.onload = function () {
      if (!img.isConnected) return;
      var picture = img.closest('picture');
      if (picture) {
        picture.querySelectorAll('source').forEach(function (source) {
          source.removeAttribute('srcset');
        });
      }
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = zoomSrc;
      img.setAttribute('data-timuntu-zoom-src', zoomSrc);
      img.setAttribute('data-timuntu-zoom-loaded', '1');
      img.removeAttribute('data-timuntu-zoom-loading');
    };
    probe.onerror = function () {
      img.removeAttribute('data-timuntu-zoom-loading');
    };
    probe.src = zoomSrc;
  }

  function originFromEvent(mainEl, event) {
    var rect = mainEl.getBoundingClientRect();
    var w = rect.width || 1;
    var h = rect.height || 1;
    var usePoint = event && typeof event.clientX === 'number' && (event.pointerType || event.detail > 0);
    if (!usePoint) {
      var img = getMainImage(mainEl);
      if (img && img.style.transformOrigin) {
        var current = img.style.transformOrigin.split(' ');
        var kx = parseFloat(current[0]);
        var ky = parseFloat(current[1]);
        if (!isNaN(kx) && !isNaN(ky)) return { x: kx, y: ky };
      }
      return { x: 50, y: 50 };
    }

    var cx = event.clientX - rect.left;
    var cy = event.clientY - rect.top;
    var level = parseInt(mainEl.getAttribute('data-timuntu-zoom-level') || '0', 10);
    var scale = ZOOM_SCALES[level] || 1;
    var oxPct = 50;
    var oyPct = 50;
    var imgEl = getMainImage(mainEl);
    if (imgEl && imgEl.style.transformOrigin) {
      var parts = imgEl.style.transformOrigin.split(' ');
      var parsedX = parseFloat(parts[0]);
      var parsedY = parseFloat(parts[1]);
      if (!isNaN(parsedX)) oxPct = parsedX;
      if (!isNaN(parsedY)) oyPct = parsedY;
    }
    var ox = (oxPct / 100) * w;
    var oy = (oyPct / 100) * h;
    var pan = getPan(mainEl);
    var px = ox + (cx - ox - pan.x) / scale;
    var py = oy + (cy - oy - pan.y) / scale;
    return {
      x: Math.max(0, Math.min(100, (px / w) * 100)),
      y: Math.max(0, Math.min(100, (py / h) * 100))
    };
  }

  function applyZoom(mainEl, level, origin) {
    var img = getMainImage(mainEl);
    if (!img) return;
    var scale = ZOOM_SCALES[level] || 1;
    if (level > 0) ensureZoomSrc(img, mainEl);
    if (level === 0) {
      mainEl.classList.remove('is-panning');
      setPan(mainEl, 0, 0);
      paintZoom(mainEl, img, 1, { x: 50, y: 50 }, { x: 0, y: 0 });
    } else {
      var prevLevel = parseInt(mainEl.getAttribute('data-timuntu-zoom-level') || '0', 10);
      var prevScale = ZOOM_SCALES[prevLevel] || 1;
      var pan = getPan(mainEl);
      if (prevScale > 1) {
        pan = compensatePan(mainEl, readOrigin(img), origin, prevScale, pan);
      } else {
        pan = { x: 0, y: 0 };
      }
      paintZoom(mainEl, img, scale, origin, pan);
    }
    mainEl.setAttribute('data-timuntu-zoom-level', String(level));
    updateZoomLabel(mainEl, level);
    syncCarouselLock(mainEl);
  }

  function onMainImageActivate(mainEl, event) {
    if (!isDesktop()) return;
    var current = parseInt(mainEl.getAttribute('data-timuntu-zoom-level') || '0', 10);
    if (current >= 2) {
      applyZoom(mainEl, 0, { x: 50, y: 50 });
      return;
    }
    applyZoom(mainEl, current + 1, originFromEvent(mainEl, event));
  }

  function bindMainImageZoom(mainEl) {
    if (!mainEl || mainEl.dataset.timuntuZoomBound === '1') return;
    mainEl.dataset.timuntuZoomBound = '1';
    var img = getMainImage(mainEl);
    if (img && !img.getAttribute('data-timuntu-zoom-src')) {
      var fromFrame = mainEl.getAttribute('data-timuntu-zoom-src');
      if (fromFrame) img.setAttribute('data-timuntu-zoom-src', fromFrame);
    }
    var panDrag = {
      pointerId: null,
      startX: 0,
      startY: 0,
      origX: 0,
      origY: 0,
      moved: false
    };
    var skipClick = false;

    function consumeSkipClick() {
      if (!skipClick) return false;
      skipClick = false;
      return true;
    }

    mainEl.addEventListener('pointerdown', function (e) {
      if (!isDesktop() || e.button !== 0) return;
      skipClick = false;
      var level = parseInt(mainEl.getAttribute('data-timuntu-zoom-level') || '0', 10);
      if (level < 1) return;
      panDrag.pointerId = e.pointerId;
      panDrag.startX = e.clientX;
      panDrag.startY = e.clientY;
      panDrag.origX = getPan(mainEl).x;
      panDrag.origY = getPan(mainEl).y;
      panDrag.moved = false;
      skipClick = false;
      try { mainEl.setPointerCapture(e.pointerId); } catch (err) {}
    });

    mainEl.addEventListener('pointermove', function (e) {
      if (panDrag.pointerId !== e.pointerId) return;
      var dx = e.clientX - panDrag.startX;
      var dy = e.clientY - panDrag.startY;
      if (!panDrag.moved && Math.abs(dx) < PAN_THRESHOLD && Math.abs(dy) < PAN_THRESHOLD) return;
      panDrag.moved = true;
      skipClick = true;
      mainEl.classList.add('is-panning');
      var level = parseInt(mainEl.getAttribute('data-timuntu-zoom-level') || '0', 10);
      if (level < 1) return;
      var imgEl = getMainImage(mainEl);
      if (!imgEl) return;
      paintZoom(
        mainEl,
        imgEl,
        ZOOM_SCALES[level] || 1,
        readOrigin(imgEl),
        { x: panDrag.origX + dx, y: panDrag.origY + dy }
      );
      e.preventDefault();
    }, { passive: false });

    function endPan(e) {
      if (panDrag.pointerId !== e.pointerId) return;
      panDrag.pointerId = null;
      mainEl.classList.remove('is-panning');
      try { mainEl.releasePointerCapture(e.pointerId); } catch (err) {}
    }

    mainEl.addEventListener('pointerup', endPan);
    mainEl.addEventListener('pointercancel', endPan);
    mainEl.addEventListener('lostpointercapture', function () {
      panDrag.pointerId = null;
      mainEl.classList.remove('is-panning');
    });

    mainEl.addEventListener('click', function (e) {
      if (consumeSkipClick()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onMainImageActivate(mainEl, e);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      skipClick = false;
      var level = parseInt(mainEl.getAttribute('data-timuntu-zoom-level') || '0', 10);
      if (level === 0) return;
      applyZoom(mainEl, 0, { x: 50, y: 50 });
    });
    if (typeof DESKTOP_MQ.addEventListener === 'function') {
      DESKTOP_MQ.addEventListener('change', function () {
        resetZoom(mainEl);
        var productRoot = mainEl.closest('product-info');
        if (productRoot) resetAllSlideZooms(productRoot);
      });
    }
  }

  function setMainImage(mainEl, url, alt, zoomUrl) {
    if (!mainEl || !url) return;
    var resolvedZoom = zoomUrl || resizeShopifyImageUrl(url, ZOOM_WIDTH);
    var img = document.createElement('img');
    img.src = url;
    img.alt = alt || '';
    img.loading = 'eager';
    img.className = 'timuntu-gallery-image';
    if (resolvedZoom) img.setAttribute('data-timuntu-zoom-src', resolvedZoom);
    mainEl.replaceChildren(img);
    if (resolvedZoom) mainEl.setAttribute('data-timuntu-zoom-src', resolvedZoom);
    resetZoom(mainEl);
  }

  function escapeAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function readJsonScript(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      return null;
    }
  }

  function resizeShopifyImageUrl(url, width) {
    if (!url) return url;
    if (/([?&]width=)\d+/.test(url)) {
      return url.replace(/([?&]width=)\d+/, '$1' + width);
    }
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function buildThumbHTML(image, index) {
    var safeAlt = escapeAttr(image.alt || '');
    var safeMain = escapeAttr(image.main || '');
    var safeThumb = escapeAttr(image.thumb || image.main || '');
    var mobileUrl = image.mobile || resizeShopifyImageUrl(image.main, 800);
    var safeMobile = escapeAttr(mobileUrl || safeMain);
    var ids = Array.isArray(image.variant_ids) ? image.variant_ids.join(',') : '';
    var safeIds = escapeAttr(ids);
    var zoomUrl = image.zoom || resizeShopifyImageUrl(image.main, ZOOM_WIDTH);
    var safeZoom = escapeAttr(zoomUrl || '');
    var loading = index === 0 ? 'eager' : 'lazy';
    return [
      '<div class="timuntu-thumbnail-item"',
        ' data-variant-ids="', safeIds, '"',
        ' data-timuntu-main-src="', safeMain, '"',
        ' data-timuntu-zoom-src="', safeZoom, '"',
        ' data-timuntu-main-alt="', safeAlt, '"',
        ' role="button" tabindex="0">',
        '<picture>',
          '<source media="(max-width: 899px)" srcset="', safeMobile, '">',
          '<source media="(min-width: 900px)" srcset="', safeThumb, '">',
          '<img src="', safeMain, '" loading="', loading, '" alt="', safeAlt, '" class="timuntu-thumbnail-image">',
        '</picture>',
      '</div>'
    ].join('');
  }

  function ensureThumbsCol(galleryCol) {
    if (!galleryCol) return null;
    var col = galleryCol.querySelector('.timuntu-thumbnails-col');
    if (!col) {
      col = document.createElement('div');
      col.className = 'timuntu-thumbnails-col';
      galleryCol.appendChild(col);
    }
    return col;
  }

  function bindThumbActivation(root, mainEl) {
    root.querySelectorAll('.timuntu-thumbnail-item[data-timuntu-main-src]').forEach(function (thumb) {
      if (thumb.dataset.timuntuBound === '1') return;
      thumb.dataset.timuntuBound = '1';
      function activate() {
        if (window.innerWidth < 900) return;
        var url = thumb.getAttribute('data-timuntu-main-src');
        var alt = thumb.getAttribute('data-timuntu-main-alt') || '';
        var zoom = thumb.getAttribute('data-timuntu-zoom-src') || '';
        setMainImage(mainEl, url, alt, zoom);
      }
      thumb.addEventListener('click', activate);
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      });
    });
  }

  function isPrimarySlide(slide) {
    var col = slide.closest('.timuntu-thumbnails-col');
    if (!col) return false;
    var colRect = col.getBoundingClientRect();
    var slideRect = slide.getBoundingClientRect();
    var center = colRect.left + colRect.width / 2;
    return slideRect.left - 1 <= center && slideRect.right + 1 >= center;
  }

  function bindMobileSlideZoom(root) {
    if (!root) return;
    var col = root.querySelector('.timuntu-thumbnails-col');
    if (!col) return;

    if (col.dataset.timuntuMobileZoomCol !== '1') {
      col.dataset.timuntuMobileZoomCol = '1';
      col.addEventListener('scroll', function () {
        if (isDesktop()) return;
        if (!col.classList.contains('is-slide-zoomed')) return;
        resetAllSlideZooms(root);
      }, { passive: true });
    }

    col.querySelectorAll('.timuntu-thumbnail-item').forEach(function (slide) {
      if (slide.dataset.timuntuMobileZoomBound === '1') return;
      slide.dataset.timuntuMobileZoomBound = '1';

      var img = getFrameImage(slide);
      if (img && !img.getAttribute('data-timuntu-zoom-src')) {
        var fromFrame = slide.getAttribute('data-timuntu-zoom-src');
        if (fromFrame) img.setAttribute('data-timuntu-zoom-src', fromFrame);
      }

      var drag = {
        pointerId: null,
        startX: 0,
        startY: 0,
        origX: 0,
        origY: 0,
        moved: false,
        skipActivate: false
      };

      function levelOf() {
        return parseInt(slide.getAttribute('data-timuntu-zoom-level') || '0', 10);
      }

      function onLevel0PointerMove(e) {
        if (isDesktop() || drag.pointerId !== e.pointerId) return;
        if (levelOf() >= 1) return;
        var dx = e.clientX - drag.startX;
        var dy = e.clientY - drag.startY;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (dist >= MOBILE_TAP_THRESHOLD) {
          drag.moved = true;
          drag.skipActivate = true;
        }
      }

      function onZoomedPointerMove(e) {
        if (isDesktop() || drag.pointerId !== e.pointerId) return;
        var level = levelOf();
        if (level < 1) return;
        var dx = e.clientX - drag.startX;
        var dy = e.clientY - drag.startY;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (!drag.moved && dist < MOBILE_TAP_THRESHOLD) return;
        drag.moved = true;
        drag.skipActivate = true;
        slide.classList.add('is-panning');
        var imgEl = getFrameImage(slide);
        if (!imgEl) return;
        paintZoom(
          slide,
          imgEl,
          ZOOM_SCALES[level] || 1,
          readOrigin(imgEl),
          { x: drag.origX + dx, y: drag.origY + dy }
        );
        e.preventDefault();
      }

      function detachZoomedMove() {
        slide.removeEventListener('pointermove', onZoomedPointerMove);
      }

      slide.addEventListener('pointerdown', function (e) {
        if (isDesktop()) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        drag.pointerId = e.pointerId;
        drag.startX = e.clientX;
        drag.startY = e.clientY;
        drag.origX = getPan(slide).x;
        drag.origY = getPan(slide).y;
        drag.moved = false;
        drag.skipActivate = false;
        if (levelOf() > 0) {
          slide.addEventListener('pointermove', onZoomedPointerMove, { passive: false });
          try { slide.setPointerCapture(e.pointerId); } catch (err) {}
        }
      }, { passive: true });

      slide.addEventListener('pointermove', onLevel0PointerMove, { passive: true });

      function endDrag(e) {
        if (isDesktop() || drag.pointerId !== e.pointerId) return;
        var level = levelOf();
        var canActivate = !drag.moved && !drag.skipActivate;
        drag.pointerId = null;
        slide.classList.remove('is-panning');
        detachZoomedMove();
        try { slide.releasePointerCapture(e.pointerId); } catch (err) {}

        if (!canActivate) return;
        if (level === 0 && !isPrimarySlide(slide)) return;
        if (level >= 2) {
          applyZoom(slide, 0, { x: 50, y: 50 });
          return;
        }
        if (level === 0) {
          col.querySelectorAll('.timuntu-thumbnail-item').forEach(function (other) {
            if (other !== slide) resetZoom(other);
          });
        }
        applyZoom(slide, level + 1, originFromEvent(slide, e));
      }

      slide.addEventListener('pointerup', endDrag);
      slide.addEventListener('pointercancel', function (e) {
        if (drag.pointerId !== e.pointerId) return;
        drag.pointerId = null;
        drag.moved = true;
        drag.skipActivate = true;
        slide.classList.remove('is-panning');
        detachZoomedMove();
        try { slide.releasePointerCapture(e.pointerId); } catch (err) {}
      });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (isDesktop()) return;
            if (levelOf() < 1) return;
            if (entry.intersectionRatio < 0.55) resetAllSlideZooms(root);
          });
        }, { root: col, threshold: [0, 0.55, 1] });
        io.observe(slide);
      }
    });
  }

  function renderGalleryFromImages(root, sectionId, images) {
    if (!images || !images.length) return;
    var mainEl = document.getElementById('MainImage-' + sectionId);
    var galleryCol = root.querySelector('.timuntu-gallery-col');
    if (!mainEl) return;

    var first = images[0];
    setMainImage(mainEl, first.main, first.alt, first.zoom);

    var thumbsCol = ensureThumbsCol(galleryCol);
    if (!thumbsCol) return;

    thumbsCol.hidden = false;
    thumbsCol.innerHTML = images.map(buildThumbHTML).join('');
    thumbsCol.scrollLeft = 0;
    thumbsCol.classList.remove('is-slide-zoomed');
    bindThumbActivation(root, mainEl);
    bindMobileSlideZoom(root);
  }

  function applyVariantMediaFilter(root, sectionId) {
    var thumbs = root.querySelectorAll('.timuntu-thumbnail-item');
    if (!thumbs.length) return;
    var mainEl = document.getElementById('MainImage-' + sectionId);
    var input = root.querySelector('.product-variant-id');
    var currentId = input && input.value ? String(input.value) : '';
    var visible = [];

    thumbs.forEach(function (thumb) {
      var raw = thumb.getAttribute('data-variant-ids') || '';
      var ids = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      var isCommon = ids.length === 0;
      var isMatch = isCommon || (currentId && ids.indexOf(currentId) !== -1);
      thumb.hidden = !isMatch;
      if (isMatch) visible.push(thumb);
    });

    if (visible.length === 0) {
      thumbs.forEach(function (t) { t.hidden = false; });
      visible = Array.prototype.slice.call(thumbs);
    }

    if (mainEl && visible[0]) {
      var url = visible[0].getAttribute('data-timuntu-main-src');
      var alt = visible[0].getAttribute('data-timuntu-main-alt') || '';
      var zoom = visible[0].getAttribute('data-timuntu-zoom-src') || '';
      if (url) setMainImage(mainEl, url, alt, zoom);
    }
  }

  function initVariantLabels(root) {
    var sectionId = root.dataset.section;
    if (!sectionId) return;
    var radios = root.querySelectorAll('.timuntu-variant-wrapper input[type="radio"]');
    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        var parts = this.id.split('-');
        var position = parts[parts.length - 2];
        var labelSpan = document.getElementById('SelectedOption-' + sectionId + '-' + position);
        if (labelSpan) {
          labelSpan.textContent = this.value;
        }
      });
    });
  }

  function attrSelector(name, value) {
    var safe = String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return '[' + name + '="' + safe + '"]';
  }

  function collectOptionNames(root) {
    var names = [];
    root.querySelectorAll('.timuntu-variant-wrapper input[type="radio"]').forEach(function (r) {
      if (r.name && names.indexOf(r.name) === -1) names.push(r.name);
    });
    return names;
  }

  function getSelectedOptions(root, optionNames) {
    return optionNames.map(function (name) {
      var checked = root.querySelector(
        '.timuntu-variant-wrapper input[type="radio"]' + attrSelector('name', name) + ':checked'
      );
      return checked ? checked.value : null;
    });
  }

  function findVariant(variants, selectedOptions) {
    if (!variants || !variants.length) return null;
    if (!selectedOptions || selectedOptions.indexOf(null) !== -1) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v.options || v.options.length !== selectedOptions.length) continue;
      var match = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        if (v.options[j] !== selectedOptions[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  function setVariantId(root, variantId) {
    if (!variantId) return;
    var sid = String(variantId);
    var inputs = root.querySelectorAll(
      '.product-variant-id, form.timuntu-add-to-cart-form input[name="id"]'
    );
    inputs.forEach(function (input) {
      if (input.value !== sid) input.value = sid;
    });
  }

  function init() {
    document.querySelectorAll('product-info[data-section]').forEach(function (root) {
      var sectionId = root.dataset.section;
      if (!sectionId) return;
      var mainEl = document.getElementById('MainImage-' + sectionId);
      if (!mainEl) return;

      var defaultGallery = readJsonScript(
        root.querySelector('script[type="application/json"][data-timuntu-default-gallery]')
      ) || [];

      var variantGalleries = {};
      root.querySelectorAll('script[type="application/json"][data-timuntu-variant-gallery]').forEach(function (script) {
        var vid = script.getAttribute('data-timuntu-variant-gallery');
        var parsed = readJsonScript(script);
        if (vid && parsed && parsed.length) {
          variantGalleries[vid] = parsed;
        }
      });

      var variantsList = readJsonScript(
        root.querySelector('script[type="application/json"][data-timuntu-variants]')
      ) || [];

      var optionNames = collectOptionNames(root);

      function resolveSelectedVariant() {
        if (!variantsList.length || !optionNames.length) return null;
        var selected = getSelectedOptions(root, optionNames);
        return findVariant(variantsList, selected);
      }

      function applyVariant(rebuild) {
        var input = root.querySelector('.product-variant-id');
        var currentId = input && input.value ? String(input.value) : '';
        var images = currentId ? variantGalleries[currentId] : null;

        if (images && images.length) {
          if (rebuild) {
            renderGalleryFromImages(root, sectionId, images);
          }
          return;
        }

        if (defaultGallery.length) {
          if (rebuild) {
            renderGalleryFromImages(root, sectionId, defaultGallery);
          }
          applyVariantMediaFilter(root, sectionId);
        }
      }

      bindMainImageZoom(mainEl);
      bindThumbActivation(root, mainEl);
      bindMobileSlideZoom(root);
      applyVariant(false);
      initVariantLabels(root);

      var radios = root.querySelectorAll('.timuntu-variant-wrapper input[type="radio"]');
      radios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          setTimeout(function () {
            var found = resolveSelectedVariant();
            if (found && found.id) {
              setVariantId(root, found.id);
              if (found.price_money) {
                var priceEl = document.getElementById('price-' + sectionId);
                if (priceEl) priceEl.textContent = found.price_money;
                root.querySelectorAll('.timuntu-submit-price').forEach(function (el) {
                  el.textContent = found.price_money;
                });
              }
              var submitBtn = root.querySelector('#ProductSubmitButton-' + sectionId);
              var submitText = submitBtn && submitBtn.querySelector('.timuntu-submit-text');
              if (submitBtn) {
                if (found.available) {
                  submitBtn.removeAttribute('disabled');
                  if (submitText) submitText.textContent = window.variantStrings ? window.variantStrings.addToCart : 'Ajouter au panier';
                } else {
                  submitBtn.setAttribute('disabled', 'disabled');
                  if (submitText) submitText.textContent = window.variantStrings ? window.variantStrings.soldOut : 'Épuisé';
                }
              }
            }
            applyVariant(true);
            resetAllSlideZooms(root);
            bindMobileSlideZoom(root);
          }, 0);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
